
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { GameType, GolfTournament, MatchFilter } from 'src/app/_interfaces/golf/golf-tournament';
import { AppStringsService } from 'src/app/_services';


@Component({
    selector: 'app-golf-match-filter',
    imports: [],
    templateUrl: './golf-match-filter.component.html',
    styleUrls: ['./golf-match-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GolfMatchFilterComponent implements OnChanges, AfterViewInit, OnInit {
    @ViewChild('scroller', { static: false }) scroller!: ElementRef<HTMLDivElement>;
    @ViewChildren('chip') chips!: QueryList<ElementRef<HTMLButtonElement>>;

    @Input({ required: true }) golfTour!: GolfTournament;
    @Input() selected: MatchFilter | null = null;
    @Output() selectedChange = new EventEmitter<MatchFilter | null>();

    filters: MatchFilter[] = [];

    private isDragging = false;
    private dragStartX = 0;
    private dragScrollLeft = 0;
    private dragMoved = false;
    private capturedPointerId: number | null = null;
    private static readonly DRAG_THRESHOLD = 6;

    constructor(private strings: AppStringsService) { }

    ngOnInit(): void {
        if (this.selected) queueMicrotask(() => this.selectedChange.emit({ ...this.selected! }));
    }

    ngOnChanges(_: SimpleChanges): void {
        this.buildFilters();
    }

    ngAfterViewInit(): void {
        this.chips.changes.subscribe(() => this.scrollSelectedIntoView('auto'));
        queueMicrotask(() => this.scrollSelectedIntoView('auto'));
    }

    private toLocalDateKey(iso?: string | null): string | null {
        if (!iso) return null;
        const d = new Date(iso);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    private tGame(gt: GameType): string {
        const key =
            gt === 'four_ball' ? 'golf.matchplay.game.fourball' :
                gt === 'four_some' ? 'golf.matchplay.game.foursomes' :
                    'golf.matchplay.game.singles';

        return this.strings.getAppString?.(key) ?? this.strings.cache.get(key) ?? key;
    }

    private shortMonthDay(dateKey: string): string {
        const [y, m, d] = dateKey.split('-').map(Number);
        return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(y, m - 1, d));
    }

    /* Sort key that primarily sorts by DATE (ascending),
       and secondarily by game-type priority within the same day. */
    private sortKeyFor(dateKey: string, gt: GameType): number {
        const dateNum = parseInt(dateKey.replace(/-/g, ''), 10);
        return dateNum * 10 + this.gameTypePriority(gt);
    }

    private defaultFrom(list: MatchFilter[]): MatchFilter | null {
        if (!list.length) return null;
        const now = new Date();

        const firstNoon = new Date(list[0].dateKey + 'T12:00:00');
        if (now < firstNoon) return list[0];

        const lastNoon = new Date(list[list.length - 1].dateKey + 'T12:00:00');
        if (now > lastNoon) return list[list.length - 1];

        let best = list[0], bestDiff = Math.abs(new Date(list[0].dateKey + 'T12:00:00').getTime() - now.getTime());
        for (let i = 1; i < list.length; i++) {
            const diff = Math.abs(new Date(list[i].dateKey + 'T12:00:00').getTime() - now.getTime());
            if (diff < bestDiff) { best = list[i]; bestDiff = diff; }
        }
        return best;
    }

    private buildFilters(): void {
        const events = (this.golfTour?.events ?? []).filter(e => e?.type === 'match_play' && !e?.is_primary_team_event);

        const map = new Map<string, MatchFilter>();
        for (const e of events) {
            const gtRaw = e?.game_type as string | null | undefined;
            const gt: GameType | null =
                gtRaw === 'four_ball' ? 'four_ball' :
                    gtRaw === 'four_some' ? 'four_some' :
                        gtRaw === 'single' ? 'single' : null;
            if (!gt) continue;

            const dateKey = this.toLocalDateKey(e?.start_date);
            if (!dateKey) continue;

            const id = `${dateKey}|${gt}`;
            if (map.has(id)) continue;

            const label = `${this.shortMonthDay(dateKey)} ${this.tGame(gt)}`;
            const sortKey = this.sortKeyFor(dateKey, gt);
            map.set(id, { dateKey, gameType: gt, label, sortKey });
        }

        this.filters = Array.from(map.values()).sort((a, b) => a.sortKey - b.sortKey);

        // set default if none is selected
        if (!this.selected) {
            this.selected = this.defaultFrom(this.filters);
            if (this.selected) queueMicrotask(() => this.selectedChange.emit({ ...this.selected! }));
        }

        setTimeout(() => this.scrollSelectedIntoView('auto'));
    }

    onPick(f: MatchFilter): void {
        if (this.dragMoved) return;
        this.selected = f;
        this.selectedChange.emit(f);
        this.scrollSelectedIntoView('smooth');
    }

    private scrollSelectedIntoView(behavior: ScrollBehavior = 'smooth') {
        if (!this.selected || !this.scroller) return;
        const key = `${this.selected.dateKey}|${this.selected.gameType}`;
        const el = this.chips.toArray().find(c => c.nativeElement.dataset['key'] === key)?.nativeElement;
        if (!el) return;

        const container = this.scroller.nativeElement;
        const cRect = container.getBoundingClientRect();
        const eRect = el.getBoundingClientRect();

        const overLeft = eRect.left - cRect.left;
        const overRight = eRect.right - cRect.right;

        let dx = 0;
        if (overLeft < 0) dx = overLeft;
        else if (overRight > 0) dx = overRight;

        if (dx !== 0) container.scrollBy({ left: dx, behavior });
        else el.scrollIntoView({ behavior, inline: 'nearest', block: 'nearest' });
    }

    onPointerDown(ev: PointerEvent): void {
        if (!this.scroller) return;
        this.isDragging = true;
        this.dragMoved = false;
        this.capturedPointerId = null;
        const el = this.scroller.nativeElement;
        this.dragStartX = ev.clientX;
        this.dragScrollLeft = el.scrollLeft;
    }

    onPointerMove(ev: PointerEvent): void {
        if (!this.isDragging || !this.scroller) return;
        const el = this.scroller.nativeElement;
        const dx = ev.clientX - this.dragStartX;

        if (Math.abs(dx) > GolfMatchFilterComponent.DRAG_THRESHOLD) {
            if (!this.dragMoved) {
                this.dragMoved = true;
                el.classList.add('dragging');
                if (this.capturedPointerId === null) {
                    el.setPointerCapture(ev.pointerId);
                    this.capturedPointerId = ev.pointerId;
                }
            }
            el.scrollLeft = this.dragScrollLeft - dx;
            ev.preventDefault();
        }
    }

    onPointerUp(ev: PointerEvent): void {
        if (!this.isDragging || !this.scroller) return;
        const el = this.scroller.nativeElement;
        this.isDragging = false;
        el.classList.remove('dragging');
        if (this.capturedPointerId !== null) {
            el.releasePointerCapture(this.capturedPointerId);
            this.capturedPointerId = null;
        }
        setTimeout(() => (this.dragMoved = false), 0);
    }

    // Order: four_some -> four_ball -> single
    private gameTypePriority(gt: GameType): number {
        return gt === 'four_some' ? 0 : gt === 'four_ball' ? 1 : 2;
    }
}
